import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, TrendingUp, TrendingDown } from 'lucide-react';
import { format } from 'date-fns';

export default function Financials() {
  const [transactions, setTransactions] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const defaultForm = { property_id: '', transaction_type: 'income', category: 'booking_revenue', amount: '', currency: 'ZAR', description: '', transaction_date: format(new Date(), 'yyyy-MM-dd'), reference: '' };
  const [form, setForm] = useState(defaultForm);

  const fetchData = async () => {
    try {
      const [t, p] = await Promise.all([api.get('/financial-transactions'), api.get('/properties')]);
      setTransactions(t.data); setProperties(p.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await api.post('/financial-transactions', { ...form, amount: Number(form.amount) });
    setDialogOpen(false); setForm(defaultForm); fetchData();
  };

  const filtered = filterType === 'all' ? transactions : transactions.filter(t => t.transaction_type === filterType);
  const totalIncome = transactions.filter(t => t.transaction_type === 'income').reduce((s, t) => s + (t.amount || 0), 0);
  const totalExpense = transactions.filter(t => t.transaction_type === 'expense').reduce((s, t) => s + (t.amount || 0), 0);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-2 border-slate-300 border-t-slate-900 rounded-full" /></div>;

  return (
    <div data-testid="financials-page">
      <div className="page-header">
        <h1 className="page-title">Financial Transactions</h1>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-transaction-btn"><Plus className="w-4 h-4 mr-2" />Add Transaction</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">New Transaction</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Property</Label>
                <Select value={form.property_id} onValueChange={v => setForm({ ...form, property_id: v })}>
                  <SelectTrigger data-testid="fin-property-select"><SelectValue placeholder="Select property" /></SelectTrigger>
                  <SelectContent>{properties.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Type</Label>
                  <Select value={form.transaction_type} onValueChange={v => setForm({ ...form, transaction_type: v })}>
                    <SelectTrigger data-testid="fin-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="income">Income</SelectItem>
                      <SelectItem value="expense">Expense</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="fin-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="booking_revenue">Booking Revenue</SelectItem>
                      <SelectItem value="cleaning_cost">Cleaning Cost</SelectItem>
                      <SelectItem value="maintenance">Maintenance</SelectItem>
                      <SelectItem value="utilities">Utilities</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Amount</Label><Input type="number" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} required data-testid="fin-amount-input" /></div>
                <div><Label>Date</Label><Input type="date" value={form.transaction_date} onChange={e => setForm({ ...form, transaction_date: e.target.value })} required data-testid="fin-date-input" /></div>
              </div>
              <div><Label>Description</Label><Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="fin-desc-input" /></div>
              <div><Label>Reference</Label><Input value={form.reference} onChange={e => setForm({ ...form, reference: e.target.value })} data-testid="fin-ref-input" /></div>
              <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" data-testid="fin-submit-btn">Create Transaction</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="stat-card">
          <div className="flex items-center gap-2 text-emerald-600 mb-1"><TrendingUp className="w-4 h-4" /><span className="text-xs font-medium">Total Income</span></div>
          <p className="text-2xl font-semibold font-heading text-slate-900">R{totalIncome.toLocaleString()}</p>
        </div>
        <div className="stat-card">
          <div className="flex items-center gap-2 text-red-600 mb-1"><TrendingDown className="w-4 h-4" /><span className="text-xs font-medium">Total Expenses</span></div>
          <p className="text-2xl font-semibold font-heading text-slate-900">R{totalExpense.toLocaleString()}</p>
        </div>
        <div className="stat-card">
          <div className="flex items-center gap-2 text-blue-600 mb-1"><TrendingUp className="w-4 h-4" /><span className="text-xs font-medium">Net</span></div>
          <p className="text-2xl font-semibold font-heading text-slate-900">R{(totalIncome - totalExpense).toLocaleString()}</p>
        </div>
      </div>

      <div className="flex gap-3 mb-4">
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-36" data-testid="filter-fin-type"><SelectValue placeholder="All" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="income">Income</SelectItem>
            <SelectItem value="expense">Expense</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="table-wrap">
        <table className="w-full data-table min-w-[600px]">
          <thead><tr><th>Date</th><th>Type</th><th>Category</th><th>Description</th><th>Amount</th><th>Reference</th></tr></thead>
          <tbody>
            {filtered.map(t => (
              <tr key={t.id} data-testid={`fin-row-${t.id}`}>
                <td>{t.transaction_date ? format(new Date(t.transaction_date), 'MMM dd, yyyy') : 'N/A'}</td>
                <td><Badge className={t.transaction_type === 'income' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}>{t.transaction_type}</Badge></td>
                <td className="capitalize">{t.category?.replace('_', ' ')}</td>
                <td>{t.description || '-'}</td>
                <td className={`font-medium ${t.transaction_type === 'income' ? 'text-emerald-700' : 'text-red-700'}`}>{t.transaction_type === 'income' ? '+' : '-'}R{(t.amount || 0).toLocaleString()}</td>
                <td className="text-sm text-slate-500">{t.reference || '-'}</td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-slate-400">No transactions</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
